import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../store/authSlice";
import { NavLink } from "react-router-dom";
import API from "../api";
import { io } from "socket.io-client";
import "./Sidebar.css";
import useNotificationsCount from "../hooks/useNotificationsCount";

const socket = io(process.env.REACT_APP_API_BASE); // e.g. http://localhost:5000

export default function Sidebar({ defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const user = useSelector(selectUser);
  const header = user?.role || "Dashboard";

  // ass filter (default to 2 when null/undefined)
  const ass = useMemo(() => user?.ass ?? 2, [user]);

  // 🔔 dynamic badge for notifications
  // const [notifCount, setNotifCount] = useState(0);

  // const fetchNotifCount = async () => {
  //   try {
  //     const resp = await API.get('/notifications', { params: { ass } });
  //     const rows = Array.isArray(resp?.data) ? resp.data : [];
  //     setNotifCount(rows.length);
  //   } catch (e) {
  //     console.error('Failed to fetch notifications count:', e?.response?.status, e?.message);
  //     // keep old count if it fails
  //   }
  // };

  // // initial + on ass change
  // useEffect(() => {
  //   fetchNotifCount();
  // }, [ass]);

  // // poll every 20s
  // useEffect(() => {
  //   const t = setInterval(fetchNotifCount, 20_000);
  //   return () => clearInterval(t);
  // }, [ass]);

  // // refresh count on socket pushes
  // useEffect(() => {
  //   const refresh = () => fetchNotifCount();
  //   socket.on('notifications:new', refresh);
  //   socket.on('notifications:removed', refresh);
  //   socket.on('notifications:cleared', refresh);
  //   return () => {
  //     socket.off('notifications:new', refresh);
  //     socket.off('notifications:removed', refresh);
  //     socket.off('notifications:cleared', refresh);
  //   };
  // }, [ass]);

  const notifCount = useNotificationsCount();

  // Sidebar sections
  const sections = [
    {
      title: " ",
      items: [
        // {
        //   to: "/tasks",
        //   label: "Tasks",
        //   icon: TasksIcon,
        //   badge: 16,
        //   exact: false,
        // },
        // 👇 use dynamic badge here
        {
          to: "/callback-notifer",
          label: "Notification",
          badge: notifCount,
          icon: LightningIcon,
        },
      ],
    },
    {
      title: "MAIN",
      items: [
        { to: "/", label: "Dashboard", icon: GridIcon, },
        { to: "/leads", label: "Leads", icon: CalendarIcon, end: true },
        { to: "/documents", label: "Applications", icon: IdIcon },
        { to: "/notes", label: "Note", icon: NoteIcon },
        { to: "/send-email", label: "Mail", icon: BoxIcon },
        { to: "/twilio/send", label: "SMS", icon: ChartIcon },
      ],
    },
    {
      title: "RECORDS",
      items: [
        { to: "/team", label: "Team", icon: UsersIcon },
      ],
    },
    {
      title: " ",
      items: [
        { to: "/admin", label: "Admin", icon: SettingsIcon },
        { to: "/support", label: "Support", icon: LifebuoyIcon },
        { to: "/profile", label: "Profile", icon: LifebuoyIcon },
      ],
    },
  ];

  return (
    <aside className={`sidebar ${collapsed ? "is-collapsed" : ""}`}>
      <div className="sidebar__top">
        <div className="sidebar__brand">
          <BrandIcon className="sidebar__brand-icon" />
          <span className="sidebar__brand-text">{header}</span>
        </div>

        <button
          className="sidebar__toggle"
          type="button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setCollapsed((v) => !v)}
        >
          <ChevronIcon direction={collapsed ? "right" : "left"} />
        </button>
      </div>

      <nav className="sidebar__scroll">
        {sections.map((section, si) => (
          <div className="sidebar__section" key={si}>
            {section.title.trim() && (
              <div className="sidebar__section-title">{section.title}</div>
            )}
            <div className="sidebar__nav">
              {section.items.map(({ to, label, icon: Icon, badge, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `sidebar__link ${isActive ? "is-active" : ""}`
                  }
                >
                  <span className="sidebar__link-icon">
                    <Icon />
                  </span>
                  <span className="sidebar__link-text">{label}</span>
                  {typeof badge === "number" && (
                    <span className="sidebar__badge">{badge}</span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {user && (
        <div className="sidebar__user">
          <div className="sidebar__avatar" aria-hidden="true">
            {user.username?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="sidebar__user-meta">
            <div className="sidebar__user-name">{user.username}</div>
            <div className="sidebar__user-mail">{user.email}</div>
          </div>
          <button className="sidebar__user-menu" aria-label="Account menu">
            <KebabIcon />
          </button>
        </div>
      )}
    </aside>
  );
}

/* icons same as your current file… */

/* ==== icons (inline, no extra deps) ==== */
function ChevronIcon({ direction = "left", ...p }) {
  const d = direction === "right" ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6";
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <path d={d} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function BrandIcon(p) {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" {...p}>
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e9d5ff" />
          <stop offset="1" stopColor="#bae6fd" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="28" height="28" rx="8" fill="url(#g)" />
      <circle cx="16" cy="16" r="6" fill="#111827" />
    </svg>
  );
}
function TasksIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <path
        d="M9 7h10M9 12h10M9 17h10M5 7h.01M5 12h.01M5 17h.01"
        strokeLinecap="round"
      />
    </svg>
  );
}
function LightningIcon(p) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}>
      <path d="M13 2 3 14h7l-1 8 11-12h-7l1-8Z" />
    </svg>
  );
}
function GridIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <rect x="3" y="3" width="7" height="7" rx="2" />
      <rect x="14" y="3" width="7" height="7" rx="2" />
      <rect x="3" y="14" width="7" height="7" rx="2" />
      <rect x="14" y="14" width="7" height="7" rx="2" />
    </svg>
  );
}
function CalendarIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  );
}
function NoteIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <path d="M4 4h10l6 6v10a2 2 0 0 1-2 2H4z" />
      <path d="M14 4v6h6" />
    </svg>
  );
}
function BoxIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <path d="M3 7l9 4 9-4M3 7l9-4 9 4M3 7v10l9 4 9-4V7" />
    </svg>
  );
}
function ChartIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" />
      <rect x="12" y="9" width="3" height="9" />
      <rect x="17" y="6" width="3" height="12" />
    </svg>
  );
}
function UsersIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <circle cx="9" cy="8" r="4" />
      <path d="M15 11a4 4 0 1 0 0-8" />
      <path d="M3 22a6 6 0 0 1 12 0" />
      <path d="M15 18a6 6 0 0 1 6 4" />
    </svg>
  );
}
function IdIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <circle cx="9" cy="10" r="3" />
      <path d="M3 17s3-3 6-3 6 3 6 3" />
    </svg>
  );
}
function SettingsIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 3.3l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .64.38 1.22.97 1.51" />
    </svg>
  );
}
function LifebuoyIcon(p) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...p}
    >
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="9" />
      <path d="M15 9l3-3M9 9 6 6M9 15l-3 3M15 15l3 3" />
    </svg>
  );
}
function KebabIcon(p) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}>
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}
